import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiMinus, FiCheckCircle } = FiIcons;

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Is this just expensive sports psychology?",
      answer: "No. This is targeted brain training for the cognitive skills that drive athletic excellence."
    },
    {
      question: "Is this a replacement for my current training?",
      answer: "Not at all. We complement your physical training by building the mental skills that make your physical abilities count.",
      details: [
        "KEEP YOUR CURRENT PROGRAM for physical skills, technique, and conditioning.",
        "ADD OUR BRAIN TRAINING to build the cognitive skills that execute those physical abilities under pressure."
      ]
    },
    {
      question: "Why would I work with a speech-language pathologist for athletics?",
      answer: "Your inner dialogue during competition—that voice saying \"don't miss,\" \"you've got this,\" or \"oh no\"—directly impacts performance. SLPs specialize in how language shapes thought. Clearer mental communication = better executive function = elite performance."
    },
    {
      question: "How is this different from visualization or mental rehearsal?",
      answer: "We target specific executive functions with evidence-based cognitive training—more targeted and effective than general visualization or positive thinking."
    },
    {
      question: "What if I already work with a sports psychologist?",
      answer: "Perfect combination. They handle motivation and confidence, we handle the brain hardware that executes performance."
    },
    {
      question: "Will this make me overthink my performance?",
      answer: "The opposite. Executive functions help with quick anticipation and adaptation in continuously changing sports situations—helping you act instinctively correctly, not overthink."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="font-semibold text-gray-900 text-lg pr-4">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0">
                  <SafeIcon
                    icon={openIndex === index ? FiMinus : FiPlus}
                    className={`h-6 w-6 transition-colors duration-200 ${
                      openIndex === index ? 'text-accent-magenta' : 'text-gray-400'
                    }`}
                  />
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6">
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-gray-700 leading-relaxed mb-4">
                          {faq.answer}
                        </p>
                        {faq.details && (
                          <div className="space-y-2">
                            {faq.details.map((detail, detailIndex) => (
                              <div key={detailIndex} className="flex items-start gap-3">
                                <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-accent-magenta mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 font-medium">
                                  {detail}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-600 mb-8">
            Still have questions? Let's talk about your specific athletic goals.
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary text-lg"
          >
            Get Your Questions Answered
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;